import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import FormField from "./FormField";
import { AUTH_HEADERS } from "../constants";
import type { AuthHeader } from "../types";
import { useTranslation } from "react-i18next";

type Props = {
  authHeader: AuthHeader;
  onAuthHeaderChange: (header: AuthHeader) => void;
  oauthClientId: string;
  onOauthClientIdChange: (value: string) => void;
  oauthClientSecret: string;
  onOauthClientSecretChange: (value: string) => void;
  apiKey: string;
  onApiKeyChange: (value: string) => void;
  bearerToken: string;
  onBearerTokenChange: (value: string) => void;
};

const inputClass =
  "h-10 w-full rounded-[8px] border border-border bg-card px-3 text-sm text-foreground outline-hidden placeholder:text-muted-foreground focus:border-primary";

const AuthHeaderSelect = ({
  value,
  onChange,
}: {
  value: AuthHeader;
  onChange: (header: AuthHeader) => void;
}) => {
  const { t, i18n } = useTranslation();
  return (
    <FormField label={t("mcpPage.fields.authHeader")}>
      <Select
        dir={i18n.dir()}
        value={value}
        onValueChange={(v) => onChange(v as AuthHeader)}
      >
        <SelectTrigger className="h-10 w-full rounded-[8px] border-border bg-card text-foreground">
          <SelectValue placeholder={t("mcpPage.actions.select")} />
        </SelectTrigger>
        <SelectContent>
          {AUTH_HEADERS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {t(`mcpPage.auth.${option.value}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormField>
  );
};

const AuthCredentialFields = ({
  authHeader,
  oauthClientId,
  onOauthClientIdChange,
  oauthClientSecret,
  onOauthClientSecretChange,
  apiKey,
  onApiKeyChange,
  bearerToken,
  onBearerTokenChange,
}: Omit<Props, "onAuthHeaderChange">) => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir();
  if (authHeader === "oauth") {
    return (
      <>
        <FormField
          label={t("mcpPage.fields.oauthClientId")}
          htmlFor="oauth-client-id"
        >
          <input
            id="oauth-client-id"
            type="text"
            dir={direction}
            autoComplete="off"
            placeholder={t("mcpPage.placeholders.oauthClientId")}
            value={oauthClientId}
            onChange={(e) => onOauthClientIdChange(e.target.value)}
            className={inputClass}
          />
        </FormField>
        <FormField
          label={t("mcpPage.fields.oauthClientSecret")}
          htmlFor="oauth-client-secret"
        >
          <input
            id="oauth-client-secret"
            type="password"
            dir={direction}
            autoComplete="off"
            placeholder={t("mcpPage.placeholders.oauthClientSecret")}
            value={oauthClientSecret}
            onChange={(e) => onOauthClientSecretChange(e.target.value)}
            className={inputClass}
          />
        </FormField>
      </>
    );
  }

  if (authHeader === "api_key") {
    return (
      <FormField
        label={t("mcpPage.fields.apiKey")}
        htmlFor="api-key"
        className="sm:col-span-2"
      >
        <input
          id="api-key"
          type="password"
          dir={direction}
          autoComplete="off"
          placeholder={t("mcpPage.placeholders.apiKey")}
          value={apiKey}
          onChange={(e) => onApiKeyChange(e.target.value)}
          className={inputClass}
        />
      </FormField>
    );
  }

  if (authHeader === "bearer") {
    return (
      <FormField
        label={t("mcpPage.fields.bearerToken")}
        htmlFor="bearer-token"
        className="sm:col-span-2"
      >
        <input
          id="bearer-token"
          type="password"
          dir={direction}
          autoComplete="off"
          placeholder={t("mcpPage.placeholders.bearerToken")}
          value={bearerToken}
          onChange={(e) => onBearerTokenChange(e.target.value)}
          className={inputClass}
        />
      </FormField>
    );
  }

  return null;
};

const AdvancedSettings = (props: Props) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(true);
  const ChevronIcon = open ? ChevronDown : ChevronRight;
  const hasCredentialFields = props.authHeader !== "none";

  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 text-sm font-medium text-foreground"
        aria-expanded={open}
      >
        <ChevronIcon
          className={`size-4 text-muted-foreground ${i18n.dir() === "rtl" && !open ? "rotate-180" : ""}`}
        />
        {t("mcpPage.advancedSettings")}
      </button>

      {open && (
        <div
          className={`mt-4 grid gap-4 ${
            hasCredentialFields
              ? "sm:grid-cols-[1fr_2fr_2fr]"
              : "sm:grid-cols-1"
          }`}
        >
          <AuthHeaderSelect
            value={props.authHeader}
            onChange={props.onAuthHeaderChange}
          />
          <AuthCredentialFields {...props} />
        </div>
      )}
    </section>
  );
};

export default AdvancedSettings;
